// ============================================================
// CineSync — Change Media Source Modal (Host Only)
// Allows host to switch between Local Movie and YouTube stream
// ============================================================

import { useState, useRef } from 'react';
import { X, Film, Upload, Check, AlertCircle, Loader2, Clipboard, CheckCircle2 } from 'lucide-react';
import { YouTubeIcon } from '@/components/icons/YouTubeIcon';
import { extractYouTubeVideoId, fetchYouTubeMeta } from '@/lib/youtube';
import { formatBytes } from '@/lib/utils';

interface ChangeMediaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToYouTube: (videoId: string, title: string) => void;
  onSwitchToLocalFile: (file: File) => void;
}

export function ChangeMediaModal({
  isOpen,
  onClose,
  onSwitchToYouTube,
  onSwitchToLocalFile,
}: ChangeMediaModalProps) {
  const [sourceType, setSourceType] = useState<'youtube' | 'local'>('youtube');

  // YouTube state
  const [youtubeInput, setYoutubeInput] = useState('');
  const [isLoadingMeta, setIsLoadingMeta] = useState(false);
  const [youtubeMeta, setYoutubeMeta] = useState<{ title: string; authorName: string; thumbnail?: string } | null>(null);
  const [youtubeError, setYoutubeError] = useState<string | null>(null);

  // Local file state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const handleYouTubeInputChange = async (val: string) => {
    setYoutubeInput(val);
    setYoutubeError(null);
    setYoutubeMeta(null);

    const videoId = extractYouTubeVideoId(val);
    if (!videoId) {
      if (val.trim().length > 10) {
        setYoutubeError('Please enter a valid YouTube URL or 11-character video ID.');
      }
      return;
    }

    setIsLoadingMeta(true);
    try {
      const meta = await fetchYouTubeMeta(videoId);
      if (meta) {
        setYoutubeMeta({
          title: meta.title,
          authorName: meta.authorName,
          thumbnail: meta.thumbnailUrl,
        });
      }
    } catch {
      setYoutubeMeta({
        title: `YouTube Video (${videoId})`,
        authorName: 'YouTube Creator',
      });
    } finally {
      setIsLoadingMeta(false);
    }
  };

  const handleSubmit = () => {
    if (sourceType === 'youtube') {
      const videoId = extractYouTubeVideoId(youtubeInput);
      if (!videoId) {
        setYoutubeError('Please enter a valid YouTube video URL or ID.');
        return;
      }
      const title = youtubeMeta?.title || `YouTube Video (${videoId})`;
      onSwitchToYouTube(videoId, title);
      onClose();
    } else {
      if (!selectedFile) {
        alert('Please select a video file.');
        return;
      }
      onSwitchToLocalFile(selectedFile);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in select-none">
      <div className="relative w-full max-w-md bg-cinema-900 border border-cinema-700/80 rounded-xl p-6 shadow-cinema-screen text-cinema-100">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-lg text-cinema-400 hover:text-cinema-100 hover:bg-cinema-800 transition-colors"
          aria-label="Close dialog"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="mb-5">
          <h2 className="text-base font-bold text-cinema-100 tracking-tight flex items-center gap-2">
            <Film className="w-4 h-4 text-lamp" />
            <span>Change Movie / Stream Source</span>
          </h2>
          <p className="text-xs text-cinema-400 mt-1">
            Switch video source for everyone in the room in real time
          </p>
        </div>

        {/* Source Type Selector */}
        <div className="flex hairline-b bg-cinema-950 p-1 rounded-lg border border-cinema-700/60 mb-5 text-xs font-semibold">
          <button
            type="button"
            onClick={() => setSourceType('youtube')}
            className={`flex-1 py-1.5 rounded flex items-center justify-center gap-1.5 transition-colors ${
              sourceType === 'youtube'
                ? 'bg-cinema-800 text-cinema-100 shadow-sm'
                : 'text-cinema-400 hover:text-cinema-200'
            }`}
          >
            <YouTubeIcon className="w-3.5 h-3.5 text-red-500" />
            <span>YouTube Video</span>
          </button>

          <button
            type="button"
            onClick={() => setSourceType('local')}
            className={`flex-1 py-1.5 rounded flex items-center justify-center gap-1.5 transition-colors ${
              sourceType === 'local'
                ? 'bg-cinema-800 text-cinema-100 shadow-sm'
                : 'text-cinema-400 hover:text-cinema-200'
            }`}
          >
            <Upload className="w-3.5 h-3.5 text-lamp" />
            <span>Local Movie File</span>
          </button>
        </div>

        {/* Content Tab: YouTube */}
        {sourceType === 'youtube' && (
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="yt-url-input" className="block text-[10px] font-semibold uppercase tracking-wider text-cinema-400">
                  YouTube URL or Video ID
                </label>
                {extractYouTubeVideoId(youtubeInput) && (
                  <span className="text-[10px] font-mono text-reel-emerald flex items-center gap-1 font-semibold">
                    <CheckCircle2 className="w-3 h-3" />
                    <span>Valid video</span>
                  </span>
                )}
              </div>
              <div className="relative">
                <input
                  id="yt-url-input"
                  type="text"
                  value={youtubeInput}
                  onChange={(e) => handleYouTubeInputChange(e.target.value)}
                  onPaste={(e) => {
                    const text = e.clipboardData?.getData('text/plain') || e.clipboardData?.getData('text') || '';
                    if (text) {
                      e.preventDefault();
                      handleYouTubeInputChange(text.trim());
                    }
                  }}
                  placeholder="Paste YouTube URL or 11-char Video ID"
                  className="w-full bg-cinema-950 border border-cinema-700 rounded-md pl-3.5 pr-24 py-2.5 text-xs text-cinema-100 placeholder-cinema-500 focus:outline-none focus:border-lamp focus:ring-1 focus:ring-lamp transition-colors"
                  autoFocus
                />
                <div className="absolute right-2 top-2 flex items-center gap-1">
                  {youtubeInput ? (
                    <button
                      type="button"
                      onClick={() => handleYouTubeInputChange('')}
                      className="p-1 rounded text-cinema-400 hover:text-cinema-100 hover:bg-cinema-800 transition-colors"
                      title="Clear link"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const text = await navigator.clipboard.readText();
                          if (text) handleYouTubeInputChange(text.trim());
                        } catch {
                          setYoutubeError('Clipboard access denied by browser. Please paste using Ctrl+V or Cmd+V.');
                        }
                      }}
                      className="flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-cinema-850 hover:bg-cinema-800 text-cinema-300 hover:text-cinema-100 border border-cinema-700/80 transition-colors shadow-xs"
                      title="Paste from clipboard"
                    >
                      <Clipboard className="w-3 h-3 text-lamp" />
                      <span>Paste</span>
                    </button>
                  )}
                  {isLoadingMeta && (
                    <Loader2 className="w-3.5 h-3.5 text-lamp animate-spin ml-1" />
                  )}
                </div>
              </div>
            </div>

            {youtubeMeta && (
              <div className="p-3 rounded-lg bg-cinema-950 border border-cinema-750 flex items-center gap-3 animate-fade-in shadow-xs">
                {youtubeMeta.thumbnail && (
                  <img
                    src={youtubeMeta.thumbnail}
                    alt="Thumbnail"
                    className="w-16 h-10 object-cover rounded bg-black flex-shrink-0 border border-cinema-700/60"
                  />
                )}
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-cinema-100 truncate">{youtubeMeta.title}</p>
                  <p className="text-[11px] text-cinema-400 truncate">{youtubeMeta.authorName}</p>
                </div>
              </div>
            )}

            {youtubeError && (
              <div className="p-2.5 rounded bg-red-500/10 border border-red-500/20 text-xs text-red-400 flex items-center gap-2 animate-fade-in">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{youtubeError}</span>
              </div>
            )}
          </div>
        )}

        {/* Content Tab: Local File */}
        {sourceType === 'local' && (
          <div className="space-y-4">
            <input
              ref={fileInputRef}
              type="file"
              accept=".mp4,.webm,.mov,.mkv"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) setSelectedFile(f);
              }}
            />

            <div
              onClick={() => fileInputRef.current?.click()}
              className="border border-dashed border-cinema-700 hover:border-cinema-550 rounded-lg p-5 text-center cursor-pointer bg-cinema-950/80 transition-colors"
            >
              {selectedFile ? (
                <div className="flex items-center gap-2.5 text-left">
                  <div className="w-9 h-9 rounded bg-lamp-dim border border-lamp/30 flex items-center justify-center text-lamp flex-shrink-0">
                    <Film className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-cinema-100 truncate">{selectedFile.name}</p>
                    <p className="text-[10px] text-cinema-400">{formatBytes(selectedFile.size)}</p>
                  </div>
                  <span className="text-[11px] text-lamp hover:underline">Change</span>
                </div>
              ) : (
                <div className="space-y-1.5 py-2">
                  <Upload className="w-6 h-6 text-cinema-400 mx-auto" />
                  <p className="text-xs font-medium text-cinema-100">Click to select movie file</p>
                  <p className="text-[10px] text-cinema-500">MP4, WebM, MOV, MKV</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action Button */}
        <div className="mt-6 flex items-center justify-end gap-2.5">
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-2 rounded-md bg-cinema-850 hover:bg-cinema-800 border border-cinema-700 text-xs font-semibold text-cinema-300 hover:text-cinema-100 transition-all duration-150 enabled:active:scale-[0.96]"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={sourceType === 'youtube' ? !extractYouTubeVideoId(youtubeInput) : !selectedFile}
            className="px-4 py-2 rounded-md bg-lamp hover:bg-lamp-bright disabled:opacity-40 disabled:cursor-not-allowed text-cinema-950 text-xs font-bold transition-all duration-150 enabled:active:scale-[0.96] shadow-md flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
          >
            <Check className="w-3.5 h-3.5" />
            <span>Switch Source</span>
          </button>
        </div>
      </div>
    </div>
  );
}
